import { gf, type GqlFieldsOf } from "@/admin/FieldConfig.tsx";
import { BooleanComponentFilter, ExactNumberFilter, TextFilter } from "@/admin/filters.tsx";
import { gql } from "@/admin/graphql.gen/gql.ts";
import { type GetUserListQuery, OrderDirection, UserOrderFields } from "@/admin/graphql.gen/graphql.ts";
import { extractData } from "@/admin/graphql/extractData.tsx";
import { GraphqlTable } from "@/admin/GraphqlTable.tsx";
import { RelatedItems } from "@/admin/RelatedItems.tsx";
import { createdAtField } from "@/admin/table_fields/standardFields.tsx";
import { AllowInvoiceFlagBadge, AllowInvoiceFlagBadgeButton } from "@/admin/users/AllowInvoiceBadge.tsx";
import { ImpersonateBlock } from "@/admin/users/ImpersonationBlock.tsx";
import { MfaBlock } from "@/admin/users/MfaBlock.tsx";
import { UserDetailView } from "@/admin/users/UserDetailView.tsx";
import { UserToolbar } from "@/admin/users/UserToolbar.tsx";
import { useConfig } from "@/config.ts";
import { useQuery } from "@apollo/client/react";
import { apiDatetimeToDate } from "@warrenio/api-spec/conversion";

const GET_USERS = gql(/* GraphQL */ `
    query GetUserList(
        $limit: Int
        $page: Int
        $orderField: UserOrderFields
        $orderDir: OrderDirection
        $search: String
        $filters: [UserFilter!]
    ) {
        admin_user_list(
            limit: $limit
            page: $page
            orderField: $orderField
            orderDir: $orderDir
            search: $search
            filters: $filters
        ) {
            items {
                id
                # signup_site
                name
                allow_invoice
                # admin
                profile_data {
                    # avatar
                    created_at
                    # updated_at
                    email
                    first_name
                    last_name
                    # personal_id_number
                    # phone_number
                }
            }
            paging {
                total
            }
        }
    }
`);

export type GQUserItem = NonNullable<GetUserListQuery["admin_user_list"]["items"]>[number];

const userFields: GqlFieldsOf<GQUserItem, UserOrderFields> = [
    gf({
        id: "user_id",
        title: "ID",
        get: (a) => a.id,
        copyable: true,
        order: UserOrderFields.Id,
        filter: ExactNumberFilter,
    }),
    gf({
        ...createdAtField,
        get: (a) => a.profile_data && apiDatetimeToDate(a.profile_data.created_at!),
        order: UserOrderFields.ProfileCreatedAt,
    }),
    gf({
        id: "name",
        title: "Username",
        get: (a) => a.name,
        order: UserOrderFields.Name,
        filter: TextFilter,
    }),
    gf({
        id: "first_name",
        title: "First Name",
        get: (a) => a.profile_data?.first_name,
        order: UserOrderFields.ProfileFirstName,
        filter: TextFilter,
    }),
    gf({
        id: "last_name",
        title: "Last Name",
        get: (a) => a.profile_data?.last_name,
        order: UserOrderFields.ProfileLastName,
        filter: TextFilter,
    }),
    gf({
        id: "allow_invoice",
        title: "Apply for Invoice",
        get: (a) => a,
        render: AllowInvoiceFlagBadgeButton,
        order: UserOrderFields.AllowInvoice,
        filter: () => <BooleanComponentFilter component={AllowInvoiceFlagBadge} />,
        // Hide by default, otherwise might be confusing for environments without an invoice payment method
        hidden: true,
    }),
    // XXX: This is broken because user-resource-interface has `__json_ignore__` on this field
    // gf({
    //     id: "is_admin",
    //     title: "Admin",
    //     get: (a) => a.admin,
    //     order: UserOrderFields.Admin,
    //     filter: BooleanFilter,
    // }),
];

export function UsersTable() {
    const { mfaAvailable } = useConfig();

    return (
        <GraphqlTable
            title="Users"
            fields={userFields}
            defaults={{ orderField: UserOrderFields.Id, orderDir: OrderDirection.Desc }}
            getId={(item) => item.id}
            useQuery={(variables) => {
                // eslint-disable-next-line react-hooks/rules-of-hooks
                const q = useQuery(GET_USERS, { variables });
                return extractData(q, (d) => d.admin_user_list);
            }}
            actionButtons={(item) => (
                <>
                    <RelatedItems id={item.id} from="fromUser" inTable />
                    <ImpersonateBlock item={item} />
                    {mfaAvailable && <MfaBlock item={item} />}
                </>
            )}
            renderDetailToolbar={(item) => <UserToolbar item={item} />}
            renderDetail={(item) => <UserDetailView key={item.id} item={item} />}
        />
    );
}
